import { Flex, useDisclosure } from '@chakra-ui/react';
import { ReactComponent as FolderIcon } from 'assets/icons/folder.svg';
import FolderImage from 'assets/images/folder.png';
import { UPGRADE_REDIRECT_BIZ_LINK } from 'constants/links';
import { CLICK_ON_PAID_FEATURE, UPGRADE } from 'constants/mixpanel';
import { useChameleon } from 'libs/third-party/Chameleon';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { BackgroundRemover } from 'modules/Editor/components/BackgroundRemover';
import { CommonGatedUpgradeModal } from 'modules/Editor/components/Modals/CommonGatedUpgradeModal';
import { ModalUpgradeTypeEnum } from 'modules/Editor/components/Modals/CommonGatedUpgradeModal.types';
import { RootState, useAppDispatch, useAppSelector, useAppStore } from 'modules/Editor/store';
import { updateWidget } from 'modules/Editor/store/infographSlice';
import { selectActiveWidgets } from 'modules/Editor/store/widgetSelector';
import { UserTypes, useUserMedia } from 'modules/User';
import { useToast } from 'modules/common/components/Toast';
import { ReactElement, memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isStandalone } from 'utils/environment';
import { setAnalyticsUpgradeFeatureProp } from 'utils/sessionStorage';
import { ImageWidgetData } from 'widgets/ImageWidget/ImageWidget.types';
import { NewWidget } from 'widgets/Widget.types';

import { FolderProps, filterByFolderImages } from './Folders/Folders.helper';
import { ManageFolders } from './Folders/ManageFolders';
import { ImageUpload } from './ImageUpload';
import { UploadImagesData } from './UploadMenu.helper';
import { UploadMenuContextProvider } from './UploadMenuContext';
import { UserImages } from './UserImages';

interface UploadMenuProps {
  onAddUploadImage: (widget: NewWidget | NewWidget[]) => void;
  disableDragDrop?: boolean;
  showBackgroundRemover?: boolean;
}

export const UploadMenu = memo(
  ({ onAddUploadImage, disableDragDrop, showBackgroundRemover = true }: UploadMenuProps): ReactElement => {
    const toast = useToast();
    const dispatch = useAppDispatch();
    const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.imagesMenu' });
    const [selectedFolder, selectFolder] = useState<string | number>('');
    const store = useAppStore();
    const activeWidgets = useAppSelector(selectActiveWidgets) || {};
    const widgetId = activeWidgets[0]?.id;
    const widgetData = (store.getState() as RootState).infograph.widgets[widgetId] as ImageWidgetData;

    const { isOpen: isUpgradeModal, onOpen: onOpenUpgradeModal, onClose: onCloseUpgradeModal } = useDisclosure();
    const { trackChameleon } = useChameleon();

    const handleChangeFolder = useCallback((selectedFolder: string | number) => {
      selectFolder(selectedFolder);
    }, []);

    const showUploadImageError = () =>
      toast({
        title: 'Failed to get user uploaded images',
        status: 'error',
      });

    const { data } = useUserMedia({ onError: !isStandalone ? showUploadImageError : undefined });

    // Think optimization to prevent re-rendering if possible
    const uploadImages: UploadImagesData = useMemo(() => {
      if (!data?.images) return [];
      return data?.images.map((imageData) => ({
        idx: imageData.id,
        url: `${process.env.REACT_APP_S3_USER_MEDIA_BUCKET_LINK}/${imageData.media_id}`,
        mediaId: imageData.media_id,
      }));
    }, [data?.images]);

    const usersFolders: FolderProps[] = useMemo(() => {
      if (!data?.folders) return [];
      return data?.folders.map((folder) => ({
        value: folder.id,
        label: folder.name,
        icon: <FolderIcon />,
        medias: folder.medias,
      }));
    }, [data?.folders]);

    const folderImages = useMemo(() => {
      const defaultFolder: FolderProps = {
        value: '',
        label: t('folders.allUploads'),
      };
      const imagesFromFolder = usersFolders.filter((i) => i.value === selectedFolder)[0] || defaultFolder;
      if (imagesFromFolder) {
        return filterByFolderImages(imagesFromFolder, uploadImages, usersFolders);
      }
      return uploadImages;
    }, [selectedFolder, t, uploadImages, usersFolders]);

    /**
     * handler to redirect users to upgrade page
     * @param featureProp
     */
    const clickUpgradeHandler = (featureProp: string) => {
      // Set mixpanel_feature session storage item used by `Analytics: Upgrade` event
      setAnalyticsUpgradeFeatureProp(featureProp);
      Mixpanel.track(UPGRADE.eventName, {
        Feature: featureProp,
      });
      trackChameleon(UPGRADE.eventName);
      window.open(UPGRADE_REDIRECT_BIZ_LINK, '_self');
    };

    /**
     * handler to control upgrade modal
     */
    const onClickUpgradeModal = () => {
      onOpenUpgradeModal();
      Mixpanel.track(CLICK_ON_PAID_FEATURE.eventName, {
        Feature: UPGRADE.feature.UploadFolder,
        plan: UserTypes.Business,
      });
    };

    const onBackgroundRemoved = useCallback(
      (newImageUrl: string) => {
        dispatch(
          updateWidget({
            widgetId,
            widgetData: {
              url: newImageUrl,
              filter: {
                backgroundRemoval: {
                  isBackgroundRemoved: true,
                  originalImageUrl: widgetData?.url || '',
                },
              },
            },
          }),
        );
      },
      [widgetId, dispatch, widgetData],
    );

    return (
      <UploadMenuContextProvider addUploadImageWidget={onAddUploadImage}>
        <Flex maxH='full' flexDir='column' overflowY='auto'>
          {showBackgroundRemover && (
            <BackgroundRemover
              style={{ marginTop: '16px', marginBottom: '-4px' }}
              imageStyle={{ width: '64px', height: '64px' }}
              sourceClicked='Upload Panel'
              onSuccessfulRemove={onBackgroundRemoved}
            />
          )}
          <ImageUpload />
          <ManageFolders
            usersFolders={usersFolders}
            uploadImages={uploadImages}
            selectedFolder={selectedFolder}
            onClickUpgradeModal={onClickUpgradeModal}
            handleChangeFolder={handleChangeFolder}
          />
          <CommonGatedUpgradeModal
            isOpen={isUpgradeModal}
            onClose={onCloseUpgradeModal}
            onClickUpgrade={clickUpgradeHandler.bind(null, UPGRADE.feature.UploadFolder)}
            upgradePlan={UserTypes.Business}
            modalImg={FolderImage}
            modalType={ModalUpgradeTypeEnum.Folder}
          />
          <UserImages uploadImages={data ? folderImages : undefined} disableDragDrop={disableDragDrop} />
        </Flex>
      </UploadMenuContextProvider>
    );
  },
);
UploadMenu.displayName = 'UploadMenu';
